import axios from "axios";
import { readItem, removeItem, writeItem } from "./localStore";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Client for the "Notizia del giorno" admin banner. GET is public; POST and
 * DELETE require the static moderator token stored (locally, opt-in) in
 * localStorage under `ol_moderator_token`. Admins paste this token once from
 * their internal tooling — the app never asks a normal user for it.
 */

const TOKEN_KEY = "ol_moderator_token";

export function getModeratorToken() {
  // Moderator-only, opt-in token pasted by an admin. Not an end-user
  // credential — a server session cookie is not applicable here.
  return readItem(TOKEN_KEY) || "";
}

export function setModeratorToken(token) {
  if (token) writeItem(TOKEN_KEY, token);
  else removeItem(TOKEN_KEY);
}

export function hasModeratorToken() {
  return getModeratorToken().length > 0;
}

function modHeaders() {
  const t = getModeratorToken();
  return t ? { "x-moderator-token": t } : {};
}

function notifyDailyNewsChanged(topicSlug) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("ol:daily-news-changed", { detail: { topicSlug } }));
}

export async function fetchDailyNews(topicSlug) {
  if (!topicSlug) return null;
  const r = await axios.get(
    `${API}/api/v1/daily-news/${encodeURIComponent(topicSlug)}`,
    { params: { _t: Date.now() }, headers: { "Cache-Control": "no-cache" }, timeout: 6000 },
  );
  return r.data?.news || null;
}

export async function publishDailyNews(topicSlug, body) {
  const r = await axios.post(
    `${API}/api/v1/daily-news/${encodeURIComponent(topicSlug)}`,
    body,
    { headers: { "Content-Type": "application/json", ...modHeaders() }, timeout: 10000 },
  );
  notifyDailyNewsChanged(topicSlug);
  return r.data?.news;
}

/** Elenco delle notizie programmate ma non ancora attive (solo moderatori). */
export async function fetchUpcomingDailyNews(topicSlug) {
  if (!topicSlug) return [];
  const r = await axios.get(
    `${API}/api/v1/daily-news/${encodeURIComponent(topicSlug)}/upcoming`,
    { headers: modHeaders(), timeout: 6000 },
  );
  return r.data?.items || [];
}

/** Rimuove una singola pubblicazione (attiva o programmata) via ID. */
export async function removeDailyNewsById(topicSlug, id) {
  const r = await axios.delete(
    `${API}/api/v1/daily-news/${encodeURIComponent(topicSlug)}/${encodeURIComponent(id)}`,
    { headers: modHeaders(), timeout: 10000 },
  );
  notifyDailyNewsChanged(topicSlug);
  return r.data;
}

export async function removeDailyNews(topicSlug) {
  const r = await axios.delete(
    `${API}/api/v1/daily-news/${encodeURIComponent(topicSlug)}`,
    { headers: modHeaders(), timeout: 10000 },
  );
  notifyDailyNewsChanged(topicSlug);
  return r.data;
}

/**
 * Uploads an image/video file for use in a daily-news banner. Returns
 * { url, kind } where kind is "image" or "video" and url is a
 * gateway-served path that can be stored as `media_url`.
 */
export async function uploadDailyNewsMedia(file) {
  const form = new FormData();
  form.append("file", file);
  const r = await axios.post(`${API}/api/v1/daily-news/media`, form, {
    headers: modHeaders(),
    timeout: 60000,
  });
  return r.data;
}

/**
 * Best-effort YouTube/Vimeo URL → embed URL. Returns null when we do not
 * recognise the URL as an embeddable video, so the caller can fall back to
 * treating it as a plain link/image URL.
 */
export function toEmbedUrl(raw) {
  const url = (raw || "").trim();
  if (!url) return null;
  // youtu.be/<id>
  let m = url.match(/^https?:\/\/youtu\.be\/([\w-]{6,})/i);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // youtube.com/watch?v=<id>
  m = url.match(/^https?:\/\/(?:www\.)?youtube\.com\/watch\?[^#]*v=([\w-]{6,})/i);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // youtube.com/shorts/<id>
  m = url.match(/^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([\w-]{6,})/i);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Already an embed URL
  if (/^https?:\/\/(?:www\.)?youtube\.com\/embed\//i.test(url)) return url;
  // vimeo.com/<id>
  m = url.match(/^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/i);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  if (/^https?:\/\/player\.vimeo\.com\/video\//i.test(url)) return url;
  return null;
}
